/**
 * Finding a hider: the shot, and what it hits.
 *
 * ============================================================================
 * THIS FILE RUNS UNMODIFIED ON BOTH THE CLIENT AND THE SERVER.
 * ============================================================================
 *
 * Same discipline as movement.ts, round.ts and possession.ts, though for a
 * weaker reason than those: nothing here is *predicted*, because the client
 * cannot predict a hit it has no way to evaluate. A hunter's snapshot names a
 * disguised hider as a prop instance and never as a player — that is the whole
 * point of M3's asymmetric views — so a hunter's client genuinely does not know
 * which of the 101 crates in front of it is a person. The server decides, the
 * client is told.
 *
 * It lives in `shared` anyway, and matters that it does, because the client
 * still needs the geometry: a crosshair that highlights what is under it, and
 * later a tracer drawn to where the shot actually stopped, must use the same
 * ray against the same world the server resolved with. A second implementation
 * in the renderer would be a second opinion about where the player is aiming.
 *
 * What lives here is only the *decision*. Cooldowns, marking a player found,
 * releasing their disguise, broadcasting the result: all side effects, all
 * owned by the caller. The same split `nextPhase` and `resolveUse` use.
 */

import { eyeHeightFor, type Hull } from "./movement.js";
import { TAG_COOLDOWN_TICKS, TAG_MISS_PENALTY_TICKS, TAG_RANGE } from "./rules.js";
import type { Aabb, StaticWorld } from "./world.js";

/**
 * What a press of Fire did. Wire values, so append-only.
 *
 * `Cooldown` is produced by the caller rather than by `resolveTag` — a shot
 * refused for being too soon never becomes a ray at all. It is in this enum
 * rather than the caller's because it is one of the three things the shooter's
 * client has to be able to display, and splitting the outcome across two
 * vocabularies would mean translating between them on the wire.
 */
export const TagOutcome = {
  Missed: 0,
  Found: 1,
  Cooldown: 2,
} as const;

export type TagOutcomeId = (typeof TagOutcome)[keyof typeof TagOutcome];

/** Nobody, in a result's `targetId`. Player ids start at 1. */
export const NO_TARGET = 0;

/**
 * Someone a shot could find.
 *
 * `hull` is the *effective* hull — crouch already applied by the caller, and
 * the prop's dimensions rather than the player's if they are disguised. This
 * takes a resolved hull rather than a player because the caller may be handing
 * over a rewound position from a history buffer rather than a live one, and a
 * struct that could hold either invites reading the wrong field.
 */
export interface TagCandidate {
  id: number;
  hull: Hull;
  /** Feet position, matching `Hull`'s anchor. */
  x: number;
  y: number;
  z: number;
}

export interface TagResult {
  outcome: TagOutcomeId;
  /** The player found, or `NO_TARGET`. */
  targetId: number;
  /**
   * Distance along the ray to whatever stopped it — a hider, a wall, or the
   * range limit if it stopped at nothing. For drawing a tracer that ends where
   * the shot ended instead of always at maximum range.
   */
  distance: number;
}

/**
 * Unit vector the player is looking along.
 *
 * Must match the camera exactly or the crosshair lies. Three.js's camera looks
 * down -Z at zero rotation and the renderer uses YXZ order with `rotation.y =
 * yaw`, `rotation.x = pitch`, which composes to this. It also agrees with
 * `step`'s wish direction at pitch 0 (`-sin(yaw)`, `-cos(yaw)`), and it has to:
 * walking forward and shooting forward are the same "forward".
 *
 * Writes into `out` rather than allocating, since the client wants this every
 * frame for the crosshair.
 */
export function aimDirection(
  yaw: number,
  pitch: number,
  out: { x: number; y: number; z: number },
): { x: number; y: number; z: number } {
  const cp = Math.cos(pitch);
  out.x = -Math.sin(yaw) * cp;
  out.y = Math.sin(pitch);
  out.z = -Math.cos(yaw) * cp;
  return out;
}

/**
 * Distance along a ray at which it enters `box`, or `-1` for no intersection
 * within `maxDist`.
 *
 * The standard slab test. A ray whose origin is already inside the box returns
 * 0, which is the honest answer and the one the caller wants: a shot fired from
 * inside a solid has hit it immediately rather than passed through it.
 *
 * The zero-direction case is checked rather than divided through. IEEE
 * arithmetic would actually carry it — `1/0` is `Infinity` and the comparisons
 * work out — but only if the origin is strictly inside the slab, and it yields
 * `NaN` on the boundary, where `NaN > tmax` is false and the ray is silently
 * accepted. An axis-aligned shot down a corridor is exactly the case that puts
 * a coordinate on a boundary.
 */
export function rayAabb(
  ox: number, oy: number, oz: number,
  dx: number, dy: number, dz: number,
  box: Aabb,
  maxDist: number,
): number {
  let tmin = 0;
  let tmax = maxDist;

  // x
  if (dx === 0) {
    if (ox < box.minX || ox > box.maxX) return -1;
  } else {
    const inv = 1 / dx;
    let t1 = (box.minX - ox) * inv;
    let t2 = (box.maxX - ox) * inv;
    if (t1 > t2) { const t = t1; t1 = t2; t2 = t; }
    if (t1 > tmin) tmin = t1;
    if (t2 < tmax) tmax = t2;
    if (tmin > tmax) return -1;
  }

  // y
  if (dy === 0) {
    if (oy < box.minY || oy > box.maxY) return -1;
  } else {
    const inv = 1 / dy;
    let t1 = (box.minY - oy) * inv;
    let t2 = (box.maxY - oy) * inv;
    if (t1 > t2) { const t = t1; t1 = t2; t2 = t; }
    if (t1 > tmin) tmin = t1;
    if (t2 < tmax) tmax = t2;
    if (tmin > tmax) return -1;
  }

  // z
  if (dz === 0) {
    if (oz < box.minZ || oz > box.maxZ) return -1;
  } else {
    const inv = 1 / dz;
    let t1 = (box.minZ - oz) * inv;
    let t2 = (box.maxZ - oz) * inv;
    if (t1 > t2) { const t = t1; t1 = t2; t2 = t; }
    if (t1 > tmin) tmin = t1;
    if (t2 < tmax) tmax = t2;
    if (tmin > tmax) return -1;
  }

  return tmin;
}

/** The box a candidate occupies. Hulls are feet-anchored; see `Hull`. */
export function candidateBox(c: TagCandidate): Aabb {
  return {
    minX: c.x - c.hull.halfWidth,
    minY: c.y,
    minZ: c.z - c.hull.halfDepth,
    maxX: c.x + c.hull.halfWidth,
    maxY: c.y + c.hull.height,
    maxZ: c.z + c.hull.halfDepth,
  };
}

/** Where a shot leaves from, given the shooter's feet and whether they crouch. */
export function shotOrigin(
  state: { x: number; y: number; z: number },
  crouching: boolean,
  out: { x: number; y: number; z: number },
): { x: number; y: number; z: number } {
  out.x = state.x;
  out.y = state.y + eyeHeightFor(crouching);
  out.z = state.z;
  return out;
}

/**
 * Resolve one shot.
 *
 * The nearest candidate wins, and a solid nearer than the nearest candidate
 * turns the shot into a miss — a wall between hunter and hider stops the bullet,
 * and so does an *unpossessed* crate, which is what makes shooting scenery a
 * miss rather than a hit on whatever stands behind it.
 *
 * Suppressed solids are skipped, which is not an optimisation but the crux of
 * the whole mechanic: a possessed prop's static box is out of the world (see
 * possession.ts) and the wearer's hull stands in its place, so a shot at a
 * disguised hider meets the candidate and not the crate they are dressed as. If
 * this respected suppression the way `StaticWorld.query` does — it cannot, since
 * `query` is a box query and this is a ray — every disguised hider would be
 * bulletproof, hidden behind the very prop they had removed from collision.
 *
 * Every solid is tested rather than walked through the broadphase grid. The grid
 * indexes boxes by XZ cell and answers box queries; a ray needs a DDA traversal
 * it does not have. depot is 188 solids and this runs once per shot rather than
 * once per tick per player, so the linear scan is not worth a second acceleration
 * structure that could disagree with the first.
 *
 * The caller owns exclusions: the shooter must not be in `candidates`, nor must
 * anyone already found, nor anyone on the shooter's own team. This decides
 * geometry, not eligibility.
 */
export function resolveTag(
  world: StaticWorld,
  shooter: { x: number; y: number; z: number; yaw: number; pitch: number },
  crouching: boolean,
  candidates: Iterable<TagCandidate>,
  range: number = TAG_RANGE,
): TagResult {
  const origin = shotOrigin(shooter, crouching, { x: 0, y: 0, z: 0 });
  const dir = aimDirection(shooter.yaw, shooter.pitch, { x: 0, y: 0, z: 0 });

  let nearestT = range;
  let targetId = NO_TARGET;

  for (const c of candidates) {
    const t = rayAabb(
      origin.x, origin.y, origin.z,
      dir.x, dir.y, dir.z,
      candidateBox(c),
      nearestT,
    );
    // `< nearestT` rather than `<=`: an exact tie goes to whoever was tested
    // first, and iteration order is the caller's, so a tie must not be able to
    // flip the answer. Ties at identical distance mean overlapping hulls, which
    // nothing in the game produces.
    if (t >= 0 && t < nearestT) {
      nearestT = t;
      targetId = c.id;
    }
  }

  // Only solids nearer than the best candidate can change the answer, so the
  // scan is bounded by `nearestT` and shrinks as it goes.
  const solids = world.solids;
  for (let i = 0; i < solids.length; i++) {
    if (world.isSuppressed(i)) continue;
    const t = rayAabb(
      origin.x, origin.y, origin.z,
      dir.x, dir.y, dir.z,
      solids[i]!,
      nearestT,
    );
    if (t >= 0 && t < nearestT) {
      nearestT = t;
      targetId = NO_TARGET;
    }
  }

  return {
    outcome: targetId === NO_TARGET ? TagOutcome.Missed : TagOutcome.Found,
    targetId,
    distance: nearestT,
  };
}

/**
 * The tick a shot fired on `frameTick` should be resolved against.
 *
 * The shooter predicted that frame at `frameTick` and was looking at everyone
 * else `INTERP_DELAY_TICKS` further back, because that is where a client
 * renders remote entities — so that is the world their crosshair was actually
 * over. Without the rewind, a hider moving at HIDER_SPEED is 0.3 m from where
 * the hunter saw them at zero latency and about 1.5 m at the harness's 150ms.
 * Hitting a moving prop stops being a matter of aim, and chasing one — the
 * whole payoff for spotting a prop that walks — stops being a play.
 *
 * Clamped rather than trusted, on both ends. `frameTick` has already been
 * through `resolveFrameTick` and so cannot name the future, but the arithmetic
 * here can still push past what a bounded history buffer holds, and a client
 * that has been silent for a while would otherwise ask to rewind further than
 * the server can answer.
 *
 * Pure, and out here rather than in the Durable Object, for the reason
 * lobby-logic.ts gives about its own two functions: this decides how far into
 * the past an unverifiable client claim can reach, which is worth asserting on
 * directly rather than reaching through a Workers runtime to exercise.
 */
export function rewindTickFor(
  frameTick: number,
  serverTick: number,
  interpDelayTicks: number,
  maxRewindTicks: number,
): number {
  const aimed = frameTick - interpDelayTicks;
  const oldest = serverTick - maxRewindTicks;
  if (aimed < oldest) return oldest;
  if (aimed > serverTick) return serverTick;
  return aimed;
}

/**
 * Ticks a hunter must wait after a shot with this outcome.
 *
 * A function rather than the caller reaching for the two constants, because the
 * server enforces this and the client displays it, and those are exactly the
 * two places a rule gets copied and then edited once.
 */
export function tagCooldownTicks(outcome: TagOutcomeId): number {
  // A refused shot never fired, so it must not extend the wait that refused it.
  if (outcome === TagOutcome.Cooldown) return 0;
  return outcome === TagOutcome.Found ? TAG_COOLDOWN_TICKS : TAG_MISS_PENALTY_TICKS;
}
